/**
 * Project Attendance Heatmap — /p/:id/heatmap
 * Live producer/AD dashboard for shooting-day crew geo-attendance.
 *
 * Renders:
 *  • Header (project · shoot date · location · call/wrap)
 *  • Callsheet date switcher (dropdown when multiple)
 *  • 4 KPI tiles  — pings · unique crew · inside · outside/not-arrived
 *  • Density heatmap  (SVG schematic, geofence-centered)
 *  • Hourly timeline (recharts bar)
 *  • Crew status table with last-seen chip
 *  • Anomaly card (farthest ping outside geofence)
 *
 * Deliberately builds the map as SVG (not a tile map) so it works
 * on-set with flaky uplinks + inside sound-stages without external
 * map tile providers.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Radar, MapPin, Clock, Users, ShieldCheck, ShieldAlert,
  Timer, Compass, ExternalLink,
} from "lucide-react";

const STATUS_COLOR = {
  inside:      "#22C55E",
  outside:     "#F97316",
  not_arrived: "#71717A",
};

const STATUS_LABEL = {
  inside:      "Inside",
  outside:     "Outside",
  not_arrived: "Not arrived",
};

export default function ProjectAttendanceHeatmap() {
  const { id } = useParams();
  const nav    = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [snapshot, setSnapshot]     = useState(null);
  const [callsheets, setCallsheets] = useState([]);
  const [selectedCsId, setSelectedCsId] = useState(null);
  const [loading, setL]             = useState(true);
  const [refreshing, setR]          = useState(false);

  async function load(csId) {
    setL(true);
    try {
      const [snapRes, csRes] = await Promise.all([
        csId
          ? api.get(`/projects/${id}/heatmap/${csId}`)
          : api.get(`/projects/${id}/heatmap/latest`),
        api.get(`/projects/${id}/heatmap/callsheets`),
      ]);
      setSnapshot(snapRes.data);
      setCallsheets(csRes.data.callsheets || []);
      setSelectedCsId(snapRes.data.callsheet.id);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Only project owners, org partners, admins, or dept heads may open the heatmap.");
        nav(`/p/${id}`); return;
      }
      if (err.response?.status === 404) {
        // no callsheet yet — still show the empty-state below
        setSnapshot(null);
        try {
          const csRes = await api.get(`/projects/${id}/heatmap/callsheets`);
          setCallsheets(csRes.data.callsheets || []);
        } catch { /* noop */ }
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Load failed");
      }
    } finally { setL(false); }
  }

  useEffect(() => { if (!authLoading && user) void load(null); /* eslint-disable-next-line */ }, [id, authLoading, user]);

  // Auto-refresh every 20s when tracker is active
  useEffect(() => {
    const trackerActive = snapshot?.callsheet?.geo_tracker_state === "active";
    if (!trackerActive || !selectedCsId) return;
    const t = setInterval(async () => {
      setR(true);
      try {
        const { data } = await api.get(`/projects/${id}/heatmap/${selectedCsId}`);
        setSnapshot(data);
      } catch { /* silent */ } finally { setR(false); }
    }, 20000);
    return () => clearInterval(t);
  }, [id, selectedCsId, snapshot?.callsheet?.geo_tracker_state]);

  if (authLoading || loading) {
    return <FullBleed>Loading…</FullBleed>;
  }
  if (!user) return null;

  if (!snapshot) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] text-white" data-testid="heatmap-empty">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <BackToProject id={id} />
          <div className="mt-6 text-[10px] tracking-[0.32em] uppercase text-emerald-400">
            On-Set · Attendance Heatmap
          </div>
          <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight mt-1">
            Waiting on today's call sheet
          </h1>
          <p className="mt-3 text-sm text-white/60 max-w-xl">
            The heatmap turns on the moment your AD publishes a call sheet
            and the crew starts pinging in. Once that happens, this page shows
            live density, hour-by-hour arrival timeline, and per-crew status.
          </p>
        </div>
      </div>
    );
  }

  const { project, callsheet, stats, hourly, density, crew_status, farthest_ping } = snapshot;
  const trackerActive = callsheet.geo_tracker_state === "active";
  const wrapDone      = callsheet.geo_tracker_state === "terminated";

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white" data-testid="heatmap-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <BackToProject id={id} />

        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <div className="text-[10px] tracking-[0.32em] uppercase text-emerald-400">
            On-Set · Attendance Heatmap
          </div>
          {trackerActive && (
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30"
              data-testid="heatmap-tracker-live">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Live · {refreshing ? "syncing…" : "auto-refresh 20s"}
            </span>
          )}
          {wrapDone && (
            <span className="text-[10px] uppercase tracking-[0.24em] px-2.5 py-1 rounded-full bg-white/5 text-white/60 border border-white/15"
              data-testid="heatmap-tracker-wrapped">
              Wrapped · frozen snapshot
            </span>
          )}
        </div>

        <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight mt-1"
          data-testid="heatmap-title">
          {project.title || "Project"}
        </h1>

        {/* Callsheet meta + switcher */}
        <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
          <div className="text-sm text-white/70 space-y-1">
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-emerald-400" />
              <span data-testid="heatmap-location">{callsheet.location || "—"}</span>
              {callsheet.geofence?.lat != null && (
                <a href={`https://www.google.com/maps?q=${callsheet.geofence.lat},${callsheet.geofence.lng}`}
                  target="_blank" rel="noreferrer"
                  className="text-[11px] text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-0.5"
                  data-testid="heatmap-open-maps">
                  <ExternalLink size={10} /> Open in Maps
                </a>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-white/45" />
              <span>Shoot {callsheet.shoot_date}</span>
              <span className="text-white/30">·</span>
              <span>Call {callsheet.call_time}</span>
              {callsheet.wrap_time && (
                <>
                  <span className="text-white/30">·</span>
                  <span>Wrap {callsheet.wrap_time}</span>
                </>
              )}
            </div>
          </div>
          {callsheets.length > 1 && (
            <div className="w-full sm:w-72">
              <Select value={selectedCsId}
                onValueChange={(v) => { setSelectedCsId(v); void load(v); }}>
                <SelectTrigger data-testid="heatmap-cs-switch"
                  className="bg-black/40 border-white/15 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {callsheets.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.shoot_date} · Call {c.call_time}
                      {c.geo_tracker_state === "active" && " · LIVE"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* KPI tiles */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="heatmap-kpi-grid">
          <Kpi label="Total pings"       value={stats.total_pings}
            Icon={Radar}   accent="#10B981" testid="kpi-total-pings" />
          <Kpi label="Unique crew"       value={stats.unique_crew}
            Icon={Users}   accent="#0EA5E9" testid="kpi-unique-crew" />
          <Kpi label="Inside geofence"   value={stats.inside_geofence}
            Icon={ShieldCheck} accent="#22C55E" testid="kpi-inside" />
          <Kpi label="Outside · not-arrived"
            value={`${stats.outside_geofence} · ${stats.not_arrived}`}
            Icon={ShieldAlert} accent="#F97316" testid="kpi-anomaly" />
        </div>

        {/* Map + Timeline */}
        <div className="mt-6 grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-2xl border border-white/10 p-5"
            data-testid="heatmap-map-card">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/55 mb-3">
              <Compass size={12} /> Density heatmap · relative to geofence
            </div>
            <SvgHeatmap
              geofence={callsheet.geofence}
              density={density}
              crew={crew_status}
            />
            <div className="mt-3 flex items-center gap-4 text-[10px] uppercase tracking-[0.22em] text-white/50 flex-wrap">
              <Legend color="#10B981" label={`Geofence · ${callsheet.geofence.radius_m}m`} />
              <Legend color="#22C55E" label="Inside · latest" />
              <Legend color="#F97316" label="Outside · latest" />
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 p-5"
            data-testid="heatmap-timeline-card">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/55 mb-3">
              <Timer size={12} /> Hourly ping timeline
            </div>
            {hourly.length === 0 ? (
              <div className="h-52 flex items-center justify-center text-xs text-white/40">
                No pings recorded yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={hourly.map(h => ({
                  hour:  h.hour.slice(11, 16),  // HH:00
                  count: h.count,
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                  <XAxis dataKey="hour" tick={{ fill: "#ffffff90", fontSize: 10 }} />
                  <YAxis allowDecimals={false} tick={{ fill: "#ffffff90", fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{ background: "#111114", border: "1px solid #2a2a2e", borderRadius: 8, fontSize: 11 }}
                    labelStyle={{ color: "#ffffff90" }}
                    itemStyle={{ color: "#10B981" }}
                  />
                  <Bar dataKey="count" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Anomaly card */}
        {farthest_ping && farthest_ping.distance_m > (callsheet.geofence?.radius_m || 0) && (
          <div className="mt-6 rounded-2xl border border-amber-400/30 bg-amber-500/10 p-5"
            data-testid="heatmap-anomaly-card">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-amber-300">
              <ShieldAlert size={12} /> Farthest ping outside geofence
            </div>
            <div className="mt-2 text-sm text-white/85">
              <b>{farthest_ping.user_name}</b> pinged at{" "}
              <b>{farthest_ping.distance_m.toLocaleString()} m</b> from the set
              — {new Date(farthest_ping.logged_at).toLocaleString()}.
            </div>
          </div>
        )}

        {/* Crew status */}
        <div className="mt-6 rounded-2xl border border-white/10 p-5"
          data-testid="heatmap-crew-card">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/55">
              <Users size={12} /> Crew status · {crew_status.length} tracked
            </div>
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-white/45">
              <StatusPill status="inside"      count={stats.inside_geofence} />
              <StatusPill status="outside"     count={stats.outside_geofence} />
              <StatusPill status="not_arrived" count={stats.not_arrived} />
            </div>
          </div>
          {crew_status.length === 0 ? (
            <div className="py-8 text-center text-white/40 text-sm">
              No crew tracked yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="heatmap-crew-table">
                <thead>
                  <tr className="text-[9px] uppercase tracking-[0.22em] text-white/40 border-b border-white/10">
                    <th className="text-left py-2">Name</th>
                    <th className="text-left py-2">Role</th>
                    <th className="text-left py-2">Status</th>
                    <th className="text-left py-2">Last ping</th>
                    <th className="text-right py-2">Distance</th>
                  </tr>
                </thead>
                <tbody>
                  {crew_status
                    .slice()
                    .sort((a, b) => {
                      // Group: inside (0), outside (1), not_arrived (2)
                      const rank = { inside: 0, outside: 1, not_arrived: 2 };
                      return (rank[a.status] || 3) - (rank[b.status] || 3);
                    })
                    .map(c => (
                    <tr key={c.user_id} className="border-b border-white/5 last:border-b-0"
                      data-testid={`heatmap-crew-row-${c.user_id}`}>
                      <td className="py-2.5 text-white/90">{c.name}</td>
                      <td className="py-2.5 text-white/60 text-xs uppercase tracking-[0.18em]">
                        {c.role || "—"}
                      </td>
                      <td className="py-2.5">
                        <StatusPill status={c.status} />
                      </td>
                      <td className="py-2.5 text-white/55 text-xs">
                        {c.last_ping_at
                          ? new Date(c.last_ping_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                          : "—"}
                      </td>
                      <td className="py-2.5 text-right text-white/60 tabular-nums text-xs">
                        {c.distance_m != null
                          ? `${Math.round(c.distance_m).toLocaleString()} m`
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════════════
function BackToProject({ id }) {
  return (
    <Link to={`/p/${id}`}
      className="text-[10px] tracking-[0.25em] uppercase text-white/55 hover:text-white inline-flex items-center gap-1.5"
      data-testid="heatmap-back">
      <ArrowLeft size={12} /> Project dashboard
    </Link>
  );
}

function FullBleed({ children }) {
  return (
    <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center text-white/40 uppercase tracking-[0.3em] text-xs">
      {children}
    </div>
  );
}

function Kpi({ label, value, Icon, accent, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.22em] text-white/50">
        <Icon size={11} style={{ color: accent }} /> {label}
      </div>
      <div className="mt-1 font-display text-3xl tabular-nums"
        style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

function StatusPill({ status, count }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full border"
      style={{
        color: STATUS_COLOR[status],
        borderColor: `${STATUS_COLOR[status]}55`,
        background:  `${STATUS_COLOR[status]}12`,
      }}
      data-testid={`status-pill-${status}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_COLOR[status] }} />
      {STATUS_LABEL[status]}{count != null && ` · ${count}`}
    </span>
  );
}


// ═══════════════════════════════════════════════════════════════════
// SVG schematic heatmap — geofence at centre, pings around it.
// Fully self-contained: no external tile provider.
// ═══════════════════════════════════════════════════════════════════
function SvgHeatmap({ geofence, density, crew }) {
  const centerLat = geofence?.lat;
  const centerLng = geofence?.lng;
  const radiusM   = geofence?.radius_m || 200;

  // Compute a display radius in metres — 2× the geofence, or auto-fit to
  // farthest ping. We convert everything into "metres from center" using
  // haversine, then normalise into the SVG box.
  const meters = useMemo(() => {
    if (centerLat == null || centerLng == null) return { max: 0, points: [], marks: [] };
    const cell = 111_320;  // approx metres per degree at equator
    const conv = (lat, lng) => {
      // Local flat-earth projection (fine for a set-sized area)
      const dLat = (lat - centerLat) * cell;
      const dLng = (lng - centerLng) * cell * Math.cos(centerLat * Math.PI / 180);
      return { x: dLng, y: -dLat, dist: Math.sqrt(dLat*dLat + dLng*dLng) };
    };
    const points = (density || []).map(d => ({ ...conv(d.lat, d.lng), count: d.count }));
    const marks  = (crew || [])
      .filter(c => c.last_lat != null)
      .map(c => ({ ...conv(c.last_lat, c.last_lng), status: c.status, name: c.name }));
    const farthest = Math.max(
      radiusM * 1.5,
      ...points.map(p => p.dist),
      ...marks.map(m => m.dist),
      1,
    );
    return { max: farthest, points, marks };
  }, [centerLat, centerLng, density, crew, radiusM]);

  const size = 420;                     // px viewport
  const pad  = 24;
  const half = size / 2;
  const scale = (half - pad) / (meters.max || 1);
  const px = (m) => m * scale;

  const maxDensity = Math.max(1, ...meters.points.map(p => p.count));

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-auto"
      style={{ maxHeight: 480 }}
      data-testid="heatmap-svg">
      <defs>
        <radialGradient id="heat" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#F97316" stopOpacity="0.9" />
          <stop offset="55%" stopColor="#F5B82E" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#F5B82E" stopOpacity="0" />
        </radialGradient>
        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none"
            stroke="#ffffff08" strokeWidth="1"/>
        </pattern>
      </defs>

      {/* Base */}
      <rect width={size} height={size} fill="#0f0f12" />
      <rect width={size} height={size} fill="url(#grid)" />

      {/* Range rings — 25/50/75/100% of view */}
      {[0.25, 0.5, 0.75, 1.0].map((f, i) => (
        <circle key={i} cx={half} cy={half} r={(half - pad) * f}
          fill="none" stroke="#ffffff10" strokeDasharray="2 4" />
      ))}

      {/* Geofence */}
      {centerLat != null && (
        <>
          <circle cx={half} cy={half} r={px(radiusM)}
            fill="#10B98115" stroke="#10B981" strokeWidth="1.5"
            strokeDasharray="6 4" />
          <text x={half + px(radiusM) + 4} y={half - 4}
            fill="#10B981" fontSize="9" letterSpacing="0.15em">
            {radiusM}m
          </text>
        </>
      )}

      {/* Density blobs */}
      {meters.points.map((p, i) => {
        const r = 18 + 22 * (p.count / maxDensity);
        return (
          <circle key={i}
            cx={half + px(p.x)} cy={half + px(p.y)}
            r={r} fill="url(#heat)" />
        );
      })}

      {/* Latest per-crew dot */}
      {meters.marks.map((m, i) => (
        <g key={i}>
          <circle cx={half + px(m.x)} cy={half + px(m.y)} r={5}
            fill={STATUS_COLOR[m.status] || "#94a3b8"}
            stroke="#0a0a0c" strokeWidth="1.5">
            <title>{m.name}</title>
          </circle>
        </g>
      ))}

      {/* Center pin */}
      <circle cx={half} cy={half} r={6} fill="#10B981" />
      <circle cx={half} cy={half} r={11} fill="none"
        stroke="#10B981" strokeOpacity="0.55" />

      {/* Compass */}
      <g fill="#ffffff45" fontSize="9" letterSpacing="0.2em">
        <text x={half}      y={14}       textAnchor="middle">N</text>
        <text x={half}      y={size - 6} textAnchor="middle">S</text>
        <text x={size - 10} y={half + 3} textAnchor="end">E</text>
        <text x={10}        y={half + 3} textAnchor="start">W</text>
      </g>
    </svg>
  );
}
